import { Trans, msg } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import KeyboardDoubleArrowDownIcon from '@mui/icons-material/KeyboardDoubleArrowDown';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PhotoCamera from '@mui/icons-material/PhotoCamera';
import {
  Badge,
  Box,
  Breadcrumbs,
  Button,
  Card,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Drawer,
  IconButton,
  Link,
  List,
  ListItem,
  ListItemText,
  Skeleton,
  styled,
  Typography,
  useTheme } from
'@mui/material';
import React, { useCallback, useEffect, useLayoutEffect, useState } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { useLocation } from 'react-router-dom';
import dayjs from 'dayjs';
import { stampCrashFormDatesIfAbsent } from '../../utils/api-dates';
import useOnlineStatus from '../../hooks/useOnlineStatus';
import useHttpApiNew from '../../hooks/useHttpApiNew';
import { NewApiBaseUrl } from '../../consts/new-api-base-url';
import { formToolbarActionButtonSx } from '../../consts/form-toolbar-button-sx';
import SvgCustomIcon from "../icons/SvgCustomIcon";
import Form from './Form';
import { canUserAccessActionHistory } from '../../pages/Statistics/statistics-report-type';

const AddIcon = ({ variant }) =>
<>
    {variant === 'person' && <SvgCustomIcon color={'white'} name={'person'} />}
    {variant === 'unit' && <SvgCustomIcon color={'white'} name={'car'} />}
    <Typography variant='h6' fontWeight={700}>+</Typography>
  </>;

const FormNavTab = ({ active, label, showLabel, theme, children }) => (
  <Box display="flex" flexDirection="column" alignItems="center" gap={0.125} sx={{ minWidth: showLabel ? 52 : undefined }}>
    {children}
    {showLabel &&
      <Typography
        variant="caption"
        sx={{
          fontSize: '0.65rem',
          fontWeight: active ? 700 : 500,
          color: active ? theme.palette.newColors.gray : theme.palette.text.secondary,
          lineHeight: 1.1,
          textAlign: 'center',
        }}
      >
        {label}
      </Typography>
    }
  </Box>
);


const ActionLogDrawer = styled(Drawer)(({ theme }) => ({
  width: theme.mixins.formDrawerWidth,
  zIndex: theme.zIndex.modal,
  '& .MuiDrawer-paper': {
    width: theme.mixins.formDrawerWidth,
    height: '100vh',
    zIndex: theme.zIndex.modal,
  }
}));

export default function FormContainer({
  token,
  open,
  isLoading,
  isMobile,
  formModel,
  formList,
  form,
  submitRef,
  handleFormOpen,
  handleSetFormNumber,
  handleCrashSubmit,
  handleAppend,
  handleRemove,
  images,
  reporterData,
  cancelCrash,
  isAdminModifyValidated = false,
  hydratedSessionInit,
  onActionLogDrawerOpenChange,
}) {
  const location = useLocation();
  const { _ } = useLingui();
  /** Prefer init idratato da Home / storage; `location.state` può mancare dopo refresh o navigazioni parziali. */
  const sessionInit = hydratedSessionInit ?? location.state?.init;
  const showCrashHistory = canUserAccessActionHistory(sessionInit?.user);
  const { control, setValue, formState, getValues, user, trigger } = useFormContext();
  const [crashId, setCrashId] = useState('');
  const [roadBadge, setRoadBadge] = useState(false);
  const [unitsBadge, setUnitsBadge] = useState(0);
  const [peopleBadge, setPeopleBadge] = useState(0);
  const [isSaveDialogOpen, setIsSaveDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [unitFormList, setUnitFormList] = useState([]);
  const [lastUnitFormIndex, setlastUnitFormIndex] = useState(0);
  const [peopleFormList, setPeopleFormList] = useState([]);
  const [lastPeopleFormIndex, setlastPeopleFormIndex] = useState(0);
  const [isReadOnly, setIsReadOnly] = useState(false);
  const [isNew, setIsNew] = useState(false);
  const [isPhotosModalOpen, setIsPhotosModalOpen] = useState(false);
  const [reporterExpanded, setReporterExpanded] = useState(false);
  const [actionLogDrawerOpen, setActionLogDrawerOpen] = useState(false);
  const [actionLogData, setActionLogData] = useState([]);
  const [actionLogLoading, setActionLogLoading] = useState(false);

  useEffect(() => {
    onActionLogDrawerOpenChange?.(actionLogDrawerOpen);
    return () => onActionLogDrawerOpenChange?.(false);
  }, [actionLogDrawerOpen, onActionLogDrawerOpenChange]);
  const prevImagesLengthRef = React.useRef(images?.length ?? 0);
  const theme = useTheme();
  const isOnline = useOnlineStatus();
  const { request } = useHttpApiNew();

  // Close photos modal only when images go from non-empty to empty (e.g. after deleting the last one), not when opening with no photos
  React.useEffect(() => {
    const currentLength = images?.length ?? 0;
    if (isPhotosModalOpen && currentLength === 0 && prevImagesLengthRef.current > 0) {
      setIsPhotosModalOpen(false);
    }
    prevImagesLengthRef.current = currentLength;
  }, [isPhotosModalOpen, images]);

  const memoizedFormModel = React.useMemo(() => formModel, [formModel]);
  const memoizedFormList = React.useMemo(() => formList, [formList]);
  const memoizedForm = React.useMemo(() => form, [form]);


  useEffect(() => {
    // create a list of units forms
    const nextUnitFormList = formList.filter((form) => /^units\.\d+$/.test(form));
    setUnitFormList(nextUnitFormList);
    const currentUnitFormListIndex = nextUnitFormList.indexOf(form.name);
    if (currentUnitFormListIndex !== -1) setlastUnitFormIndex(currentUnitFormListIndex);else
    if (nextUnitFormList.length === 0) setlastUnitFormIndex(0);
    // create a list of people - include both nested (units.0.people.0) and direct (people.0)
    const nextPeopleFormList = formList.filter((form) => {
      // Match nested people: units.0.people.0
      if (/^(units\.\d+\.)people\.\d+$/.test(form)) return true;
      // Match direct people: people.0, people.1, etc. (for hospitals)
      if (/^people\.\d+$/.test(form)) return true;
      return false;
    });
    // Always sync (including []): otherwise a previous render kept people.0 while formList became ["crash"] only → indexOf -1
    setPeopleFormList(nextPeopleFormList);
    const currentPeopleFormListIndex = nextPeopleFormList.indexOf(form.name);
    if (currentPeopleFormListIndex !== -1) setlastPeopleFormIndex(currentPeopleFormListIndex);else
    if (nextPeopleFormList.length === 0) setlastPeopleFormIndex(0);
  }, [formList, form.name]);

  const basePath = String(formList[form.number] ?? '');
  const fieldArrayName = form.type === 'units' || form.type === 'people' ?
  `${basePath.split('.').slice(0, 2).join('.')}.people` :
  form.type;

  const { append, remove } = useFieldArray({
    control,
    name: fieldArrayName
  });

  const openSaveDialog = () => {setIsSaveDialogOpen(true);};

  const closeSaveDialog = () => {setIsSaveDialogOpen(false);};

  const openDeleteDialog = () => {setIsDeleteDialogOpen(true);};

  const closeDeleteDialog = () => {setIsDeleteDialogOpen(false);};

  const requestCrashFormSubmit = useCallback(() => {
    stampCrashFormDatesIfAbsent(setValue, getValues);
    queueMicrotask(() => submitRef.current?.requestSubmit());
  }, [setValue, getValues, submitRef]);

  useLayoutEffect(() => {
    if (!open) return;
    stampCrashFormDatesIfAbsent(setValue, getValues);
  }, [open, setValue, getValues]);

  const getFormCtxUserOrg = () => {
    const doAs = user?.doAsOrganization;
    if (doAs != null && doAs !== -1) return doAs;
    return sessionInit?.user?.organization ?? user?.organization;
  };

  const fetchActionLogAndOpenDrawer = useCallback(async () => {
    const currentCrashId = getValues()?.crash?.['Crash id'];
    if (!currentCrashId) return;
    setActionLogLoading(true);
    setActionLogDrawerOpen(true);
    try {
      const url = new URL(`${NewApiBaseUrl}/ActionLog`);
      url.searchParams.set('limit', '500');
      url.searchParams.set('crash', currentCrashId);
      const res = await request(url.toString(), 'GET', null, { Authorization: `Bearer ${token}` });
      const data = Array.isArray(res?.responseData) ? res.responseData : [];
      setActionLogData(data);
    } catch (err) {

      setActionLogData([]);
    } finally {
      setActionLogLoading(false);
    }
  }, [getValues, request, token]);

  const getLogIcon = useCallback((log) => {
    const org = log.organization === -1 ? log.organization : log.userFk?.basic_organization;
    switch (org) {
      case 1:return 'police';
      case 2:return 'healthCrossFill';
      case 3:return 'insurance';
      default:return 'shieldPerson';
    }
  }, []);

  // children = Children.map(children, el => cloneElement(el, { submitRef, handleSubmit }))

  useEffect(() => {

    const form = getValues();
    setCrashId(form.crash['Crash id']);
    setIsNew(form.isNew);
    if (Object.prototype.hasOwnProperty.call(form, 'units')) {
      setUnitsBadge(form.units.length);
      setRoadBadge(true);
      setPeopleBadge(form.units.map((unit) => unit.people?.length).reduce((a, c) => a + c, 0));
    }
    if (Object.prototype.hasOwnProperty.call(form, 'people') && form?.people?.length) {
      setPeopleBadge(form.people.length);
    }
  }, [formState, getValues]);

  useEffect(() => {
    setIsReadOnly(!!getValues('crash.merge_date'));
  }, [crashId, getValues]);

  const canShowCrashDelete = !isReadOnly || isAdminModifyValidated;

  let StyledDrawer;
  if (isMobile) {
    StyledDrawer = styled(Drawer)(({ theme }) => ({
      '& .MuiDrawer-paper': {
        height: `calc(100dvh - ${theme.mixins.toolbar.minHeight}px)`,
        width: '100dvw'
      }
    }));
  } else {
    StyledDrawer = styled(Drawer)(({ theme }) => ({
      position: 'relative' // imp
      , width: theme.mixins.formDrawerWidth,
      '& .MuiDrawer-paper': {
        width: theme.mixins.formDrawerWidth,
        height: `calc(100vh - ${theme.mixins.toolbar.minHeight}px)`,
        position: 'absolute' // imp
        , transition: 'none !important'
      }
    }));
  }

  return (
    <>
    <StyledDrawer
        variant="persistent"
        open={open}
        anchor={isMobile ? 'bottom' : theme.direction === 'rtl' ? 'right' : 'left'}>
        
      {isMobile && <Box sx={{ textAlign: 'center', pt: 1 }}>
        <IconButton onClick={() => handleFormOpen(false)}>
          <KeyboardDoubleArrowDownIcon />
        </IconButton>
      </Box>}
      <Box display='flex' flexDirection='column' px={1} pt={0.5} pb={0.75} position='relative'>
        {!isMobile &&
          <>
            {showCrashHistory &&
            <IconButton
              onClick={() => {
                if (actionLogDrawerOpen) {
                  setActionLogDrawerOpen(false);
                } else {
                  fetchActionLogAndOpenDrawer();
                }
              }}
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                zIndex: 1,
                ...(actionLogDrawerOpen ? { backgroundColor: theme.palette.newColors.gray } : {})
              }}
              aria-label="Crash history">
              
              <SvgCustomIcon name="history" size={24} color={actionLogDrawerOpen ? 'white' : undefined} />
            </IconButton>
            }
            <IconButton
              onClick={() => cancelCrash()}
              sx={{
                position: 'absolute',
                top: 0,
                right: 0,
                zIndex: 1
              }}>
              
              <CloseIcon />
            </IconButton>
          </>
          }
        <Typography width='100%' variant='button' sx={{ color: theme.palette.newColors.gray }} fontWeight={700} textAlign='center'>
          {isNew ? <Trans> ADD NEW CRASH RECORD</Trans> : <Trans>CRASH RECORD</Trans>}
        </Typography>
        <Typography textTransform='uppercase' sx={{ color: theme.palette.newColors.gray, lineHeight: 1.2 }} width='100%' variant='h6' fontWeight={600} textAlign='center'>
          {`${form.displayName}${form.type === 'units' ? ' ' + (lastUnitFormIndex + 1) : form.type === 'people' ? ' ' + (lastPeopleFormIndex + 1) : ''}`}
        </Typography>
        {isMobile &&
          <Breadcrumbs sx={{ alignSelf: 'center' }} aria-label={_(msg`breadcrumb`)}>
            {form.name !== 'crash' && <Link
              component="button"
              onClick={() => {
                const idx = formList.indexOf('crash');

                handleSetFormNumber(idx);
              }}>
              
              Crash
            </Link>}
            {formList.includes('road') && form.name === 'road' &&
            <Link
              component="button"
              onClick={() => {
                const idx = formList.indexOf('road');

                handleSetFormNumber(idx);
              }}>
              
                Road
              </Link>
            }
            {(form.name ?? '').match(/\w+\.\d+/g)?.map((match) =>
            <Link
              component="button"
              key={match}
              onClick={() => {
                const idx = formList.indexOf(match);

                handleSetFormNumber(idx);
              }}>
              
                {match.split('.').map((m) => {
                if (!isNaN(Number.parseInt(m))) return Number.parseInt(m) + 1;
                return m.
                replace('units', 'Unit').
                replace('people', 'Person');
              }).join(' ')
              }
              </Link>
            )}
          </Breadcrumbs>
          }
        <Box mt={0.5} display='flex' justifyContent='space-evenly' alignItems='flex-start' width='100%'>
          <FormNavTab active={form.type === 'crash'} label={<Trans>Crash</Trans>} showLabel={!isMobile} theme={theme}>
            <Badge
              color='success'
              overlap='circular'
              variant='dot'
              invisible={formState.submitCount === 0}>
              <IconButton
                sx={form.type === 'crash' ? { backgroundColor: theme.palette.newColors.gray } : {}}
                onClick={async () => {
                  if (form.type !== 'crash') {
                    const idx = formList.indexOf('crash');
                    const ok = await trigger();
                    if (!ok) return;
                    handleSetFormNumber(idx);
                  }
                }}>
                <SvgCustomIcon name={'info'} color={form.type === 'crash' ? 'white' : theme.palette.newColors.gray} />
              </IconButton>
            </Badge>
          </FormNavTab>
          {Object.prototype.hasOwnProperty.call(formModel, 'road') &&
            <FormNavTab active={form.type === 'road'} label={<Trans>Road</Trans>} showLabel={!isMobile} theme={theme}>
              <Badge
                color='success'
                overlap='circular'
                variant='dot'
                invisible={!roadBadge}>
                <IconButton
                  sx={form.type === 'road' ? { backgroundColor: theme.palette.newColors.gray } : {}}
                  onClick={async () => {
                    if (form.type !== 'road') {
                      const idx = formList.indexOf('road');
                      const ok = await trigger();
                      if (!ok) return;
                      handleSetFormNumber(idx);
                    }
                  }}>
                  <SvgCustomIcon name={'road'} color={form.type === 'road' ? 'white' : theme.palette.newColors.gray} />
                </IconButton>
              </Badge>
            </FormNavTab>}
          {Object.prototype.hasOwnProperty.call(formModel, 'units') &&
            <FormNavTab active={form.type === 'units'} label={<Trans>Units</Trans>} showLabel={!isMobile} theme={theme}>
              <Badge
                color='primary'
                overlap='circular'
                badgeContent={unitsBadge}>
                <IconButton
                  sx={form.type === 'units' ? { backgroundColor: theme.palette.newColors.gray } : {}}
                  onClick={async () => {
                    const ok = await trigger();
                    if (!ok) return;
                    let nextUnitForm;
                    const currentIndex = unitFormList.indexOf(form.name);
                    if (currentIndex !== -1) {
                      if (currentIndex + 1 < unitFormList.length) {
                        nextUnitForm = unitFormList[currentIndex + 1];
                      } else nextUnitForm = unitFormList[0];
                    } else {
                      nextUnitForm = unitFormList[lastUnitFormIndex];
                    }
                    const nextIndex = formList.indexOf(nextUnitForm ?? form.name);
                    handleSetFormNumber(nextIndex);
                  }}>
                  <SvgCustomIcon name={'car'} color={form.type === 'units' ? 'white' : theme.palette.newColors.gray} />
                </IconButton>
              </Badge>
            </FormNavTab>}
          {Object.prototype.hasOwnProperty.call(formModel, 'people') &&
            <FormNavTab active={form.type === 'people'} label={<Trans>People</Trans>} showLabel={!isMobile} theme={theme}>
              <Badge
                color='primary'
                overlap='circular'
                badgeContent={peopleBadge}>
                <IconButton
                  sx={form.type === 'people' ? { backgroundColor: theme.palette.newColors.gray } : {}}
                  onClick={async () => {
                    const ok = await trigger();
                    if (!ok) return;
                    let nextPeopleForm;
                    const currentIndex = peopleFormList.indexOf(form.name);
                    if (currentIndex !== -1) {
                      if (currentIndex + 1 < peopleFormList.length) {
                        nextPeopleForm = peopleFormList[currentIndex + 1];
                      } else {
                        nextPeopleForm = peopleFormList[0];
                      }
                    } else {
                      nextPeopleForm = peopleFormList[lastPeopleFormIndex] || peopleFormList[0] || form.name;
                    }
                    const nextIndex = formList.indexOf(nextPeopleForm);
                    if (nextIndex !== -1) {
                      handleSetFormNumber(nextIndex);
                    }
                  }}>
                  <SvgCustomIcon name={'person'} color={form.type === 'people' ? 'white' : theme.palette.newColors.gray} />
                </IconButton>
              </Badge>
            </FormNavTab>}
        </Box>
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
        {!!Object.keys(reporterData).length &&
          <Card variant="outlined" sx={{ m: 0, flexShrink: 0, width: '100%', boxSizing: 'border-box', overflow: 'hidden' }}>
            <Box sx={{ width: '100%' }}>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                onClick={() => setReporterExpanded((prev) => !prev)}
                sx={{
                  cursor: 'pointer',
                  userSelect: 'none',
                  width: '100%',
                  py: 0.5,
                  px: 1,
                  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
                  '&:hover': { backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)' },
                  transition: 'background-color 0.2s',
                  borderBottom: reporterExpanded ? 1 : 0,
                  borderColor: 'divider'
                }}
                aria-expanded={reporterExpanded}>
                
                <Typography sx={{ color: theme.palette.text.secondary, fontSize: 12, fontWeight: 600 }}>
                  <Trans>Reported by</Trans>
                </Typography>
                <IconButton size="small" sx={{ p: 0.25 }} aria-label={reporterExpanded ? _(msg`collapse`) : _(msg`expand`)}>
                  <ExpandMoreIcon sx={{ transform: reporterExpanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s', fontSize: 20 }} />
                </IconButton>
              </Box>
              <Collapse sx={{ marginLeft: 0 }} in={reporterExpanded} timeout="auto" unmountOnExit>
                <Box sx={{ width: '100%', px: 1.5, pt: 1, pb: 1.5 }}>
                  <Typography sx={{ fontSize: 13 }} component='div'>
                    {`${reporterData.name} ${reporterData.surname}`}
                  </Typography>
                  <Typography sx={{ fontSize: 12 }} gutterBottom component='div'>
                    {reporterData.department}
                  </Typography>
                  <Typography sx={{ color: 'text.secondary', fontSize: 11 }}>
                    <Trans>Telephone</Trans>
                  </Typography>
                  <Typography gutterBottom={!!reporterData.email} sx={{ fontSize: 13 }} component='div'>
                    {reporterData.phone}
                  </Typography>
                  {reporterData.email && <>
                    <Typography sx={{ color: 'text.secondary', fontSize: 11 }}>
                      <Trans>email</Trans>
                    </Typography>
                    <Typography sx={{ fontSize: 13 }} component='div'>
                      {reporterData.email}
                    </Typography>
                  </>}
                </Box>
              </Collapse>
            </Box>
          </Card>
          }
        <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          <Form
              token={token}
              isMobile={isMobile}
              submitRef={submitRef}
              formModel={memoizedFormModel}
              formList={memoizedFormList}
              form={memoizedForm}
              init={sessionInit}
              // unitsFormArray={{ fields: unitsMethods.fields }}
              handleCrashSubmit={handleCrashSubmit}
              handleAppend={handleAppend}
              handleSave={''}
              images={images}
              isReadOnly={isReadOnly}
              isAdminModifyValidated={isAdminModifyValidated}
              isPhotosModalOpen={isPhotosModalOpen}
              onOpenPhotosModal={() => setIsPhotosModalOpen(true)}
              onClosePhotosModal={() => setIsPhotosModalOpen(false)} />
            
        </Box>
        <Box display='flex' flexDirection='column' gap={0.25} mt={0.5} mb={0.25} pl={1} pr={1} sx={{ flexShrink: 0 }}>
          {isMobile && isLoading && <CircularProgress />}
          {form.type === 'crash' && getFormCtxUserOrg() !== 2 &&
            <Box display='flex' justifyContent='center' gap={1} alignItems='center' flexWrap='wrap'>
              {(!isReadOnly || !!images?.length || isAdminModifyValidated) &&
              <Button
                type="button"
                variant="contained"
                aria-label={_(msg`view photos`)}
                onClick={() => setIsPhotosModalOpen(true)}
                sx={formToolbarActionButtonSx(theme, 'photo')}>
                
                  <PhotoCamera sx={{ color: 'white' }} fontSize="small" />
                  <Typography component="span" sx={{ color: 'white', fontWeight: 700, fontSize: '0.95rem' }}>
                    {images?.length ? images.length : '+'}
                  </Typography>
                </Button>
              }
              {canShowCrashDelete &&
              <Button sx={formToolbarActionButtonSx(theme, 'delete')} variant='contained' onClick={() => openDeleteDialog()}>
                  <SvgCustomIcon name={'delete'} color={'white'} />
                </Button>}
            </Box>}
          {form.type === 'crash' && getFormCtxUserOrg() === 2 &&
            <Box display='flex' justifyContent='center' gap={1} alignItems='center' flexWrap='wrap'>
              {(!isReadOnly || !!images?.length || isAdminModifyValidated) &&
              <Button
                type="button"
                variant="contained"
                aria-label={_(msg`view photos`)}
                onClick={() => setIsPhotosModalOpen(true)}
                sx={formToolbarActionButtonSx(theme, 'photo')}>
                
                  <PhotoCamera sx={{ color: 'white' }} fontSize="small" />
                  <Typography component="span" sx={{ color: 'white', fontWeight: 700, fontSize: '0.95rem' }}>
                    {images?.length ? images.length : '+'}
                  </Typography>
                </Button>
              }
              {!isReadOnly &&
              <Button sx={formToolbarActionButtonSx(theme, 'add')} variant='contained' onClick={() => {handleAppend('people');}}>
                  <AddIcon variant='person' />
                </Button>}
              {canShowCrashDelete &&
              <Button sx={formToolbarActionButtonSx(theme, 'delete')} variant='contained' onClick={() => openDeleteDialog()}>
                  <SvgCustomIcon name={'delete'} color={'white'} />
                </Button>}
            </Box>}
          {form.type === 'road' &&
            <Box display='flex' justifyContent='center' gap={1} alignItems='center' flexWrap='wrap'>
              {!isReadOnly &&
              <Button sx={formToolbarActionButtonSx(theme, 'add')} variant='contained' onClick={() => {handleAppend('units');}}>
                  <AddIcon variant='unit' />
                </Button>}
              {canShowCrashDelete &&
              <Button sx={formToolbarActionButtonSx(theme, 'delete')} variant='contained' onClick={() => openDeleteDialog()}>
                  <SvgCustomIcon name={'delete'} color={'white'} />
                </Button>}
            </Box>}
          {form.type === 'units' &&
            <>
              <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'center', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                {!isReadOnly &&
                <Button sx={formToolbarActionButtonSx(theme, 'add')} variant='contained' onClick={() => {handleAppend('units');}}>
                    <AddIcon variant='unit' />
                  </Button>}
                {!isReadOnly &&
                <Button sx={formToolbarActionButtonSx(theme, 'add')} variant='contained' onClick={() => {handleAppend('unitPeople', formList[form.number], append);}}>
                    <AddIcon variant='person' />
                  </Button>}
                {!isReadOnly &&
                <Button sx={formToolbarActionButtonSx(theme, 'delete')} variant='contained' onClick={() => handleRemove('units', form)}>
                    <SvgCustomIcon name={'delete'} color={'white'} />
                  </Button>}
              </Box>
            </>}
          {form.type === 'people' && getFormCtxUserOrg() !== 2 &&
            <Box display='flex' justifyContent='center' gap={1} alignItems='center' flexWrap='wrap'>
              {!isReadOnly &&
              <Button sx={formToolbarActionButtonSx(theme, 'add')} variant='contained' onClick={() => {handleAppend('unitPeople', formList[form.number], append);}}>
                  <AddIcon variant='person' />
                </Button>}
              {!isReadOnly &&
              <Button sx={formToolbarActionButtonSx(theme, 'delete')} variant='contained' onClick={() => handleRemove('unitPeople', form, remove)}>
                  <SvgCustomIcon name={'delete'} color={'white'} />
                </Button>}
            </Box>}
          {form.type === 'people' && getFormCtxUserOrg() === 2 &&
            <Box display='flex' justifyContent='center' gap={1} alignItems='center' flexWrap='wrap'>
              {!isReadOnly &&
              <Button sx={formToolbarActionButtonSx(theme, 'add')} variant='contained' onClick={() => {handleAppend('people', formList[form.number], append);}}>
                  <AddIcon variant='person' />
                </Button>}
              {!isReadOnly &&
              <Button sx={formToolbarActionButtonSx(theme, 'delete')} variant='contained' onClick={() => handleRemove('people', form)}>
                  <SvgCustomIcon name={'delete'} color={'white'} />
                </Button>}
            </Box>}
        </Box>
        <Box
            display='flex'
            mt={0.5}
            pr={1}
            pl={1}
            gap={1}
            mb={isMobile ? `calc(${theme.spacing(2)} + ${theme.mixins.toolbar.minHeight}px)` : 0.5}>
            
          {isMobile && isLoading ?
            <CircularProgress /> :
            <Box sx={{ position: 'relative', width: '100%', mt: 0.5 }}>
              {/* Linea centrale */}
              <Box
                sx={{
                  height: 2,
                  background: theme.palette.newColors.lightGray,
                  width: isMobile ? '90%' : '100%',
                  marginInlineStart: isMobile ? '5%' : 0,
                  position: 'absolute',
                  top: '50%',

                  zIndex: 0
                }} />
              

              {/* Bottoni sopra la linea */}
              <Box
                sx={{
                  position: 'relative',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  width: isMobile ? '90%' : '100%',
                  marginInlineStart: isMobile ? '5%' : 0,
                  zIndex: 1
                }}>
                
                <Button
                  sx={{
                    background: theme.palette.newColors.lightGray,
                    borderRadius: '50%',
                    width: 36,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: 36,
                    minWidth: 0,
                    '&:hover': {
                      background: theme.palette.newColors.lightGray
                    },
                    p: 0
                  }}
                  disabled={form.number === 0}
                  onClick={async () => {

                    const ok = await trigger();
                    if (!ok) return;
                    handleSetFormNumber('-');
                  }}>
                  
                  <SvgCustomIcon name={'arrowLeft'} size={16} color={form.number === 0 ? theme.palette.newColors.gray : theme.palette.newColors.lighGray} />
                </Button>

                {!isReadOnly &&
                <Button
                  sx={{
                    background: theme.palette.newColors.gray,
                    '&:hover': {
                      background: theme.palette.newColors.gray
                    },
                    borderRadius: '50%',
                    width: 36,
                    height: 36,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minWidth: 0,
                    p: 0
                  }}
                  disabled={!getValues().units?.length && !getValues().people?.length}
                  onClick={() => {
                    setValue('isFinal', true);
                    // submitRef.current.requestSubmit()
                    openSaveDialog();
                  }}>
                  
                    <SvgCustomIcon name={'save'} color={theme.palette.newColors.lightGray} />
                  </Button>}
                {isReadOnly && isAdminModifyValidated &&
                <Button
                  sx={{
                    background: theme.palette.newColors.gray,
                    '&:hover': {
                      background: theme.palette.newColors.gray
                    },
                    borderRadius: '50%',
                    width: 36,
                    height: 36,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minWidth: 0,
                    p: 0
                  }}
                  onClick={() => openSaveDialog()}
                  title={_(msg`Save`)}>
                  
                    <SvgCustomIcon name={'save'} color={theme.palette.newColors.lightGray} />
                  </Button>}
                <Button
                  sx={{
                    background: theme.palette.newColors.lightGray,
                    borderRadius: '50%',
                    width: 36,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: 36,
                    minWidth: 0,
                    '&:hover': {
                      background: theme.palette.newColors.lightGray
                    },
                    p: 0
                  }}
                  disabled={form.number === formList.length - 1}
                  onClick={async () => {

                    const ok = await trigger();
                    if (!ok) return;
                    handleSetFormNumber('+');
                  }}>
                  
                  <SvgCustomIcon size={16} name={'arrowRight'} />
                </Button>
              </Box>
            </Box>}
        </Box>
      </Box>
      <Dialog open={isSaveDialogOpen} onClose={closeSaveDialog}>
        <DialogTitle sx={{ color: theme.palette.newColors.gray }}>
          {isReadOnly ?
            <Trans>Save changes?</Trans> :
            !isOnline ?
            <Trans>Save your report (offline)</Trans> :

            <Trans>What do you want to do?</Trans>
            }
        </DialogTitle>
        <DialogContent>
          {isReadOnly ?
            <DialogContentText>
              <Trans>Do you want to save the changes to this crash record?</Trans>
            </DialogContentText> :
            isNew ?
            isOnline ?
            <DialogContentText>
                <Trans>
                  Save as Draft will store the crash record temporarily.
                  <br />
                  It can be edited later and will not appear in any database until it is saved as Final.
                </Trans>
                <br />
                <br />
                <Trans>
                  Save as Final will store the crash record in your institution’s database.
                  <br />
                  Final crashes are ready for official validation.
                </Trans>
              </DialogContentText> :

            <DialogContentText>
                <Trans>
                  Save offline stores a complete report ready to upload: it will be queued to sync with your institution
                  when you are back online, and can be included in the offline export (ZIP) from the toolbar.
                </Trans>
              </DialogContentText> :

            isOnline ?
            <DialogContentText>
              <Trans>
                Save as Final will modify the crash record in your institution’s database.
                <br />
                Final crashes are ready for official validation.
              </Trans>
            </DialogContentText> :

            <DialogContentText>
              <Trans>
                Your changes will be saved on this device and queued to sync when you are back online.
              </Trans>
            </DialogContentText>
            }
        </DialogContent>
        <DialogActions>
          <Button variant='outlined' color='error' onClick={closeSaveDialog}>
            <Trans>Cancel</Trans>
          </Button>
          {isReadOnly ?
            isOnline &&
            <Button
              variant='outlined'
              sx={{ color: theme.palette.newColors.gray, fontWeight: 600, border: `` }}
              onClick={() => {
                setValue('isFinal', true, { shouldDirty: true, shouldTouch: true });
                closeSaveDialog();
                queueMicrotask(() => requestCrashFormSubmit());
              }}
              autoFocus>
              
                <Trans>Save</Trans>
              </Button> :


            <>
              {isNew && isOnline &&
              <Button
                variant='outlined'
                color='success'
                onClick={() => {
                  setValue('isDraft', true, { shouldDirty: true, shouldTouch: true });
                  setValue('isFinal', true, { shouldDirty: true, shouldTouch: true });
                  closeSaveDialog();
                  queueMicrotask(() => requestCrashFormSubmit());
                }}>
                
                  <Trans>Save as draft</Trans>
                </Button>
              }
              {isNew && isOnline &&
              <Button
                variant='outlined'
                sx={{ color: theme.palette.newColors.gray, fontWeight: 600, border: `` }}
                onClick={() => {
                  setValue('isDraft', false, { shouldDirty: true, shouldTouch: true });
                  setValue('isFinal', true, { shouldDirty: true, shouldTouch: true });
                  closeSaveDialog();
                  queueMicrotask(() => requestCrashFormSubmit());
                }}
                autoFocus>
                
                  <Trans>Save as final</Trans>
                </Button>
              }
              {isNew && !isOnline &&
              <Button
                variant='outlined'
                sx={{ color: theme.palette.newColors.gray, fontWeight: 600, border: `` }}
                onClick={() => {
                  setValue('isDraft', false, { shouldDirty: true, shouldTouch: true });
                  setValue('isFinal', true, { shouldDirty: true, shouldTouch: true });
                  closeSaveDialog();
                  queueMicrotask(() => requestCrashFormSubmit());
                }}
                autoFocus>
                
                  <Trans>Save offline</Trans>
                </Button>
              }
              {!isNew && isOnline &&
              <Button
                variant='outlined'
                sx={{ color: theme.palette.newColors.gray, fontWeight: 600, border: `` }}
                onClick={() => {
                  setValue('isDraft', false, { shouldDirty: true, shouldTouch: true });
                  setValue('isFinal', true, { shouldDirty: true, shouldTouch: true });
                  closeSaveDialog();
                  queueMicrotask(() => requestCrashFormSubmit());
                }}
                autoFocus>
                
                  <Trans>Save as final</Trans>
                </Button>
              }
              {!isNew && !isOnline &&
              <Button
                variant='outlined'
                sx={{ color: theme.palette.newColors.gray, fontWeight: 600, border: `` }}
                onClick={() => {
                  setValue('isDraft', false, { shouldDirty: true, shouldTouch: true });
                  setValue('isFinal', true, { shouldDirty: true, shouldTouch: true });
                  closeSaveDialog();
                  queueMicrotask(() => requestCrashFormSubmit());
                }}
                autoFocus>
                
                  <Trans>Save offline</Trans>
                </Button>
              }
            </>
            }
        </DialogActions>
      </Dialog>
      <Dialog open={isDeleteDialogOpen} onClose={closeDeleteDialog}>
        <DialogTitle>
          <Trans>Are you sure?</Trans>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            <Trans>
              The crash will be permanently deleted!
            </Trans>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
              variant='outlined'
              color='success'
              onClick={closeDeleteDialog}
              autoFocus>
              
            <Trans>Cancel</Trans>
          </Button>
          <Button
              variant='outlined'
              color='error'
              onClick={() => cancelCrash(crashId)}>
              
            <Trans>Yes, delete</Trans>
          </Button>
        </DialogActions>
      </Dialog>
    </StyledDrawer>

    {showCrashHistory &&
    <ActionLogDrawer
        variant="persistent"
        anchor={theme.direction === 'rtl' ? 'left' : 'right'}
        open={actionLogDrawerOpen}>
        
      <Box sx={{ p: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography
              sx={{
                color: theme.palette.mode === 'light' ? theme.palette.newColors.gray : 'white',
                fontWeight: 'bold',
                fontSize: 20,
                flex: 1,
                textAlign: 'center'
              }}>
              
            <Trans>Crash history</Trans>
          </Typography>
          <IconButton
              onClick={() => setActionLogDrawerOpen(false)}
              sx={{
                color: theme.palette.mode === 'light' ? theme.palette.newColors.gray : 'white',
                '&:hover': {
                  backgroundColor: theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.04)' : 'rgba(255, 255, 255, 0.08)'
                }
              }}>
              
            <CloseIcon />
          </IconButton>
        </Box>
        <Divider sx={{ color: theme.palette.mode === 'light' ? theme.palette.newColors.gray : 'white' }} />
      </Box>
      {actionLogLoading ?
        <>
          <Skeleton width="100%" sx={{ mt: 2, fontSize: '2rem' }} />
          {Array.from({ length: 5 }).map((_, i) =>
          <Skeleton key={i} width="100%" sx={{ mx: 1 }} />
          )}
        </> :

        <Box overflow="auto" sx={{ flex: 1 }}>
          <Typography
            sx={{
              textAlign: 'center',
              fontSize: 14,
              fontWeight: 'bold',
              color: theme.palette.mode === 'light' ? theme.palette.newColors.gray : 'white'
            }}>
            
            {_(msg`Found items:`)} {actionLogData?.length ?? 0}
          </Typography>
          {actionLogData?.length > 0 &&
          <List>
              {actionLogData.map((log, index) => {
              const username = !log.username && !log.userFk ? 'admin' : log?.userFk?.username || log?.username;
              const iconName = getLogIcon(log);
              return (
                <ListItem
                  key={log.id ?? index}
                  sx={{
                    flexDirection: 'column',
                    alignItems: 'stretch',
                    backgroundColor: index % 2 === 0 ? theme.palette.grey[200] : theme.palette.background.default
                  }}>
                  
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                      <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 1 }}>
                        <SvgCustomIcon name={iconName} />
                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                          <Typography variant="body2" sx={{ fontSize: 14, fontWeight: 'bold' }}>
                            {username}
                          </Typography>
                          <Typography variant="body2" sx={{ fontSize: 14, fontStyle: 'oblique' }}>
                            {log.ts ? dayjs(log.ts).format('DD/MM/YYYY HH:mm') : ''}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                    <ListItemText primaryTypographyProps={{ fontSize: 14 }} primary={log.message} />
                  </ListItem>);

            })}
            </List>
          }
        </Box>
        }
    </ActionLogDrawer>
    }
    </>);

}